import type { EsProduct } from '~layers/app/types/elastic'
import type { LocationQueryValue } from 'vue-router'

interface _CatalogVariables {
  search?: string | LocationQueryValue[]
  page?: number
  filters?: unknown[]
  itemsPerPage?: number
  sortOrder?: string
  sortDirection?: 'ASC' | 'DESC'
  sequenceProfile?: unknown
  [key: string]: unknown
}

interface CatalogMeta {
  [key: string]: unknown
}

export function useCatalog(meta: CatalogMeta) {
  const products = useState<EsProduct[]>('products', () => [])
  const filterUrlMap = useState<Record<string, string>>('filterUrlMap', () => ({}))

  const data = ref()
  const loading = ref(false)
  const error = ref()

  const route = useRoute()

  const {
    search,
  } = useCatalogSearch()

  const {
    defaultSort,
    page,
    sort,
    sortOptions,
    itemsPerPage,
    totalCount,
  } = useCatalogPaginator()

  const {
    rawFilters,
    filtersFromRoute,
  } = useCatalogFilters(meta)

  const variables = computed(() => {
    const sortOrder = sort.value === defaultSort && meta.value?.sort ? meta.value?.sort : sort.value
    const sortOption = sortOptions.find(sortOption => sortOption.key === sortOrder)

    return {
      search: search.value,
      page: page.value,
      filters: filtersFromRoute.value,
      itemsPerPage: itemsPerPage.value,
      sortOrder: sortOption?.field,
      sortDirection: sortOption?.direction,
      sequenceProfile: (meta.value as Record<string, unknown>)?.sequence_profile,
      path: route.path.replace(/^\s*\/*\s*|\s*\/*\s*$/gm, ''),
    }
  })

  async function fetchProductData() {
    loading.value = true

    const { data: response } = await useAsyncGraphqlQuery('plp', variables.value, useGraphqlDefaultClient())

    data.value = response?.value?.data

    if (response?.value?.errors?.length) {
      error.value = response.value.errors
      console.error(error.value)
    }

    if (!data.value) {
      return
    }

    totalCount.value = data?.value?.esProductList?.totalCount || 0
    rawFilters.value = data?.value?.esProductList?.filters || []
    products.value = data?.value?.esProductList?.products || []

    // Build a map of filter options that have a hard SEO url in esUrl.
    // Only urls with exactly one distinguishing facet (beyond the page's base
    // filters) are considered, so we can precisely link a single filter option.
    const base = new Set<string>()
    for (const bf of (meta.value as Record<string, any>)?.filters || []) {
      for (const v of bf.values || []) {
        base.add(`${bf.key}::${v}`)
      }
    }

    const urlMap: Record<string, string> = {}
    if (!route.query.filter) {
      for (const u of data?.value?.esUrlList?.urls || []) {
        const extras = [...new Set(
          (u.filters || []).flatMap((f: any) =>
            (f.values || []).map((v: any) => `${f.key}::${v}`)),
        )].filter((id: string) => !base.has(id))

        if (extras.length === 1) {
          urlMap[extras[0] as string] = u.url.replace(/^\/|\/$/g, '')
        }
      }
    }
    filterUrlMap.value = urlMap

    loading.value = false
  }

  watch(variables, fetchProductData)

  return {
    products,
    fetchProductData,
  }
}
